import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface AuthGuardProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

export function AuthGuard({ children, allowedRoles }: AuthGuardProps) {
  const { user, profile, loading } = useAuth();
  const location = useLocation(); // Get current location

  console.log(`[AuthGuard] Evaluating route: ${location.pathname}`);
  console.log(`[AuthGuard] State: loading=${loading}, user=${!!user}, profile=${!!profile}`);
  
  if (loading) {
    console.log('[AuthGuard] Auth context loading, showing spinner.');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!user) {
    console.log('[AuthGuard] No user found, redirecting to /login.');
    // Store the intended destination to redirect back after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If user exists, but profile hasn't loaded yet (edge case after login, before profile fetch completes)
  // We could show loading here too, but AuthContext loading should cover this.
  // If we reach here and profile is null, it might indicate a problem with profile fetching/creation.
  if (!profile) {
      console.warn(`[AuthGuard] User exists (ID: ${user.id}), but profile is null. This might indicate a profile fetch issue or incomplete sign-up.`);
      // Redirecting to /profile might be appropriate here to force completion/check
      // Or, if roles are required, redirect to unauthorized/error
      if (allowedRoles) {
          console.log('[AuthGuard] Profile missing, but roles are required. Redirecting to /unauthorized.');
          return <Navigate to="/unauthorized" replace />;
      } else {
           console.log('[AuthGuard] Profile missing, but no specific roles required. Allowing access (might redirect to /profile later).');
           // Allow access, assuming the target component (like UserProfile) handles the null profile state.
      }
  }

  // Check roles only if profile exists and roles are specified
  if (allowedRoles && profile && !allowedRoles.includes(profile.role)) {
    console.log(`[AuthGuard] Role mismatch. User role: '${profile.role}', Allowed roles: ${allowedRoles.join(', ')}. Redirecting to /unauthorized.`);
    return <Navigate to="/unauthorized" replace />;
  }

  // If user is logged in, profile (if needed) is loaded, and roles match (if needed)
  console.log('[AuthGuard] Access granted.');
  return <>{children}</>;
}